export type Bleed = { left: number; right: number };

/** Nearest ancestor that has a box; tables mount inside `display: contents` wrappers. */
export const layoutParent = (el: Element) => {
	let parent = el.parentElement;
	while (parent && getComputedStyle(parent).display === "contents") {
		parent = parent.parentElement;
	}
	return parent;
};

/**
 * How far `el` must reach past its parent's content box, each side, to meet
 * the inside edges of `bounds`.
 */
export const bleedTo = (el: Element, bounds: HTMLElement): Bleed => {
	const parent = layoutParent(el);
	if (!parent) return { left: 0, right: 0 };
	const style = getComputedStyle(parent);
	const parentLeft =
		parent.getBoundingClientRect().left +
		parent.clientLeft +
		parseFloat(style.paddingLeft);
	const parentRight =
		parent.getBoundingClientRect().left +
		parent.clientLeft +
		parent.clientWidth -
		parseFloat(style.paddingRight);
	const boundsLeft = bounds.getBoundingClientRect().left + bounds.clientLeft;
	return {
		left: parentLeft - boundsLeft,
		right: boundsLeft + bounds.clientWidth - parentRight,
	};
};

/**
 * Reports {@link bleedTo} now and again whenever it changes. Measured from the
 * parent rather than `el`, whose margins are what the caller sets from it, so
 * applying them can't trigger another change.
 */
export const observeBleed = (
	el: Element,
	bounds: HTMLElement,
	onChange: (bleed: Bleed) => void,
) => {
	let last: Bleed | undefined;
	const measure = () => {
		const next = bleedTo(el, bounds);
		if (last?.left === next.left && last?.right === next.right) return;
		last = next;
		onChange(next);
	};
	measure();
	const observer = new ResizeObserver(measure);
	observer.observe(bounds);
	const parent = layoutParent(el);
	if (parent) observer.observe(parent);
	return () => observer.disconnect();
};
